import type posthogType from "posthog-js";
import type { DraftPick, GameMode, SeasonResult } from "./game/types";

export const POSTHOG_PROJECT_TOKEN = import.meta.env.VITE_POSTHOG_KEY ?? "phc_gX45mqjUUW5Qg3sX1Qr7dK4hORNzaa83qyodLdfSoHv";
export const POSTHOG_PROJECT_ID = "238642";

const consentStorageKey = "serie-a-38-0-analytics-consent";
const defaultApiHost = import.meta.env.VITE_POSTHOG_HOST ?? "/ingest";
const defaultUiHost = import.meta.env.VITE_POSTHOG_UI_HOST ?? "https://us.posthog.com";

type AnalyticsConsent = "accepted" | "declined";
type PostHogClient = typeof posthogType;

let initialized = false;
let pageviewCaptured = false;
let clientPromise: Promise<PostHogClient> | null = null;

export function initAnalytics() {
  if (shouldCaptureAnalytics()) {
    void ensurePostHog();
  }
}

export function getAnalyticsConsent(): AnalyticsConsent | null {
  if (typeof window === "undefined") {
    return null;
  }

  const saved = window.localStorage.getItem(consentStorageKey);
  return saved === "accepted" || saved === "declined" ? saved : null;
}

export function acceptAnalyticsConsent() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(consentStorageKey, "accepted");
  void ensurePostHog().then((client) => {
    client.opt_in_capturing();
    captureInitialPageview(client);
    client.capture("cookie_consent_granted", { project_id: POSTHOG_PROJECT_ID });
  });
}

export function declineAnalyticsConsent() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(consentStorageKey, "declined");
  if (clientPromise) {
    void clientPromise.then((client) => client.opt_out_capturing());
  }
}

export function trackModeSelected(mode: GameMode) {
  capture("mode_selected", {
    mode_id: mode.id,
    mode_name: mode.name,
    requires_club_choice: mode.requiresClubChoice,
  });
}

export function trackFormationSelected(formationId: string, formationName: string) {
  capture("formation_selected", {
    formation_id: formationId,
    formation_name: formationName,
  });
}

export function trackFavoriteClubSelected(club: string, modeId: string) {
  capture("favorite_club_selected", {
    club,
    mode_id: modeId,
  });
}

export function trackDraftPick(pick: DraftPick, round: number, mode: GameMode) {
  capture("draft_pick_selected", {
    round,
    mode_id: mode.id,
    slot_id: pick.slot.id,
    slot_label: pick.slot.label,
    player_id: pick.player.id,
    club: pick.player.club,
    season: pick.player.season,
    has_source: Boolean(pick.player.source),
    has_evidence: Boolean(pick.player.evidence?.length),
  });
}

export function trackDraftCompleted(picks: DraftPick[], result: SeasonResult, mode: GameMode) {
  capture("draft_completed", {
    mode_id: mode.id,
    picks: picks.length,
    wins: result.wins,
    draws: result.draws,
    losses: result.losses,
    points: result.points,
    rating: result.rating,
    chemistry: result.chemistry,
  });
}

export function trackShareAction(action: "copy_challenge" | "share_callout_view" | "share_callout_click" | "shared_challenge_started") {
  capture("share_action", { action });
}

export function trackAppInterestSubmitted(platform: string, hasEmail: boolean) {
  capture("app_interest_submitted", { platform, has_email: hasEmail });
}

export function trackFeedbackOpened(source: "footer" | "result") {
  capture("feedback_opened", { source });
}

export function trackFeedbackSubmitted(category: string) {
  capture("feedback_submitted", { category });
}

function capture(event: string, properties?: Record<string, unknown>) {
  if (!shouldCaptureAnalytics()) {
    return;
  }

  void ensurePostHog().then((client) => client.capture(event, properties));
}

function shouldCaptureAnalytics() {
  return getAnalyticsConsent() !== "declined";
}

function captureInitialPageview(client: PostHogClient) {
  if (pageviewCaptured || typeof window === "undefined") {
    return;
  }

  pageviewCaptured = true;
  client.capture("$pageview", {
    path: window.location.pathname,
    project_id: POSTHOG_PROJECT_ID,
  });
}

function ensurePostHog() {
  if (!clientPromise) {
    clientPromise = import("posthog-js").then((module) => {
      const client = module.default;
      if (!initialized) {
        initialized = true;
        client.init(POSTHOG_PROJECT_TOKEN, {
          api_host: defaultApiHost,
          ui_host: defaultUiHost,
          capture_pageview: false,
          opt_out_capturing_by_default: false,
          person_profiles: "identified_only",
          persistence: "localStorage+cookie",
          loaded: (loadedClient) => {
            if (shouldCaptureAnalytics()) {
              captureInitialPageview(loadedClient as PostHogClient);
            }
          },
        });
      }
      return client;
    });
  }

  return clientPromise;
}
